import {
  BarChart3,
  BarChartHorizontalBig,
  BookOpen,
  Code,
  CalendarDays,
  Table,
  LayoutGrid,
  LineChart,
  List,
  Newspaper,
  TrendingUp,
  GitCompareArrows,
  ScatterChart,
  BarChart,
  Layers,
  Star,
  SwatchBook,
  MessageSquare,
  Sparkles,
  Waypoints,
  Activity,
  PieChart,
  ShieldAlert,
  ListOrdered,
  Tags,
  Grid3x3,
  Gauge,
  Clock,
  BellRing,
  Radar,
  Scale,
  Dices,
  Crosshair,
  Filter,
} from 'lucide-react'
import type { ComponentType } from '@/types/schemas/workspace'
import type { Feature, PlanTier } from '@/context/plan-constraint-utils'
import { REALTIME_ENABLED } from '@/services/realtime/flags'

export const COMPONENT_META: Record<ComponentType, { label: string; icon: React.ElementType }> = {
  chart: { label: 'Chart', icon: LineChart },
  'bar-chart': { label: 'Bar Chart', icon: BarChartHorizontalBig },
  scanner: { label: 'Scanner', icon: BarChart3 },
  'multi-scanner': { label: 'Multi-Scanner', icon: Layers },
  'key-stats': { label: 'Key Stats', icon: Table },
  watchlist: { label: 'Watchlist', icon: Star },
  news: { label: 'News', icon: Newspaper },
  'ticker-stats': { label: 'Ticker Stats', icon: TrendingUp },
  calendar: { label: 'Calendar', icon: CalendarDays },
  'history-records': { label: 'Records', icon: Table },
  'history-chart': { label: 'Chart', icon: LineChart },
  'history-stats': { label: 'Market Stats', icon: BarChart3 },
  'history-news': { label: 'News', icon: Newspaper },
  'history-comparison': { label: 'Comparison', icon: GitCompareArrows },
  'history-time-series': { label: 'Time Series', icon: TrendingUp },
  'history-scatter': { label: 'Scatter Plot', icon: ScatterChart },
  'history-bar': { label: 'Bar Chart', icon: BarChart },
  'history-bar-category': { label: 'Bar by Category', icon: BarChartHorizontalBig },
  'history-donut-category': { label: 'Donut by Category', icon: PieChart },
  'history-intraday': { label: 'Price Action Graph', icon: BarChart },
  'history-calendar': { label: 'Calendar Heatmap', icon: CalendarDays },
  'history-sankey': { label: 'Flow Graph', icon: Waypoints },
  'history-scorecard': { label: 'Scorecard', icon: Gauge },
  'history-heatmap': { label: 'Heatmap', icon: Grid3x3 },
  'history-time-distribution': { label: 'Timing Breakdown', icon: Clock },
  // NOTE: 'history-briefing' kept in COMPONENT_META (required by Record<ComponentType, ...>)
  // for backward compat with persisted layouts. The panel has been removed and the type
  // is NOT included in HISTORY_TYPES — it will not appear in the add-panel menu.
  'history-briefing': { label: 'Briefing', icon: Sparkles },
  'tags-explorer': { label: 'Tags', icon: Tags },
  // NOTE: 'history-deep-dive' is retired (audit D11). Kept in COMPONENT_META
  // (required by Record<ComponentType, ...>) and out of HISTORY_TYPES, so it
  // never appears in the add-panel menu.
  'history-deep-dive': { label: 'Deep Dive', icon: MessageSquare },
  'history-multi-chart': { label: 'Chart Grid', icon: LayoutGrid },
  notebook: { label: 'Notebook', icon: BookOpen },
  'alert-log': { label: 'Alert Log', icon: List },
  'price-alerts': { label: 'Price Alerts', icon: BellRing },
  'alert-windows': { label: 'Scan Alerts', icon: Radar },
  'multi-chart': { label: 'Chart Scanner', icon: LayoutGrid },
  'sector-heatmap': { label: 'Heatmap', icon: Grid3x3 },
  'theme-tracker': { label: 'Theme Tracker', icon: SwatchBook },
  'market-overwatch': { label: 'Market Overwatch', icon: Gauge },
  'script-editor': { label: 'Script Editor', icon: Code },
  'scanner-parity': { label: 'Scanner Parity', icon: Scale },
  // ── Backtest report panels (not shown in dashboard/history add-panel menus) ─
  // 'backtest-metrics' (Key Metrics strip) is addable in Run mode only; the
  // strategy Report page renders the same strip above its mosaic.
  'backtest-metrics': { label: 'Key Metrics', icon: BarChart3 },
  'backtest-overview': { label: 'Overview', icon: BarChart },
  'backtest-performance': { label: 'Performance', icon: TrendingUp },
  'backtest-trades': { label: 'Trades', icon: Activity },
  'backtest-calendar': { label: 'Calendar', icon: CalendarDays },
  'backtest-distributions': { label: 'Distributions', icon: PieChart },
  'backtest-buckets': { label: 'Price Buckets', icon: BarChartHorizontalBig },
  'backtest-symbols': { label: 'Symbols', icon: Table },
  'backtest-risk': { label: 'Risk Profile', icon: ShieldAlert },
  'backtest-wins-losses': { label: 'Wins vs. Losses', icon: GitCompareArrows },
  'backtest-streaks': { label: 'Streaks', icon: BarChart3 },
  'backtest-trade-chart': { label: 'Trade Chart', icon: TrendingUp },
  'backtest-trade-fills': { label: 'Fills', icon: BarChart3 },
  'backtest-daily-pnl': { label: 'Daily P&L', icon: BarChart },
  'backtest-win-rate': { label: 'Win Rate', icon: TrendingUp },
  'backtest-monthly': { label: 'P&L by Period', icon: BarChart3 },
  'backtest-monthly-table': { label: 'Monthly Overview', icon: BarChart3 },
  'backtest-hold-times': { label: 'Hold Times', icon: BarChart3 },
  'backtest-dist-hour': { label: 'Hour Distribution', icon: BarChart3 },
  'backtest-dist-day': { label: 'Day Distribution', icon: BarChart3 },
  'backtest-dist-duration': { label: 'Duration Distribution', icon: BarChart3 },
  'backtest-return-histogram': { label: 'Return Distribution', icon: BarChart },
  'backtest-day-metrics': { label: 'Day Metrics', icon: BarChart3 },
  'backtest-rolling-windows': { label: 'Rolling Windows', icon: BarChart3 },
  'backtest-filter-lab': { label: 'Filtered vs original', icon: Filter },
  'backtest-field-explorer': { label: 'Field Explorer', icon: BarChart },
  'backtest-monte-carlo': { label: 'Monte Carlo', icon: Dices },
  'backtest-monte-carlo-bands': { label: 'Where this run sits', icon: Crosshair },
  'backtest-is-oos': { label: 'In-sample / Out-of-sample', icon: GitCompareArrows },
  'backtest-period-stability': { label: 'Period Stability', icon: CalendarDays },
  'backtest-baseline': { label: 'Baseline', icon: Scale },
  'backtest-sizing-settings': { label: 'Sizing Summary', icon: Scale },
  'backtest-sizing-equity': { label: 'Sized Balance', icon: TrendingUp },
  'backtest-not-taken': { label: 'Not Taken', icon: List },
  'backtest-general-metrics': { label: 'General Metrics', icon: BarChart3 },
  'backtest-equity-curve': { label: 'Equity Curve', icon: TrendingUp },
  'backtest-strategy-rollup': { label: 'Strategy Rollup', icon: BarChart3 },
  // ── Run-specific panels (registered types; no add-panel menu lists them) ────
  'backtest-run-trades': { label: 'Executions', icon: ListOrdered },
  'backtest-chart-grid': { label: 'Chart Grid', icon: LayoutGrid },
  // ── Journal panels (fixed rail pages; no add-panel menu lists them) ────────
  'journal-edge-rating': { label: 'Edge Rating', icon: Gauge },
  'journal-discipline': { label: 'Plan Discipline', icon: ShieldAlert },
  'journal-calendar': { label: 'Journal Calendar', icon: CalendarDays },
  'journal-daily-pnl': { label: 'Daily P&L', icon: BarChart3 },
  'journal-day-metrics': { label: 'Day Metrics', icon: BarChart3 },
  'journal-day-trades': { label: "Day's Trades", icon: ListOrdered },
  'journal-day-plans': { label: "Day's Plans", icon: List },
  'journal-day-pnl': { label: 'Intraday P&L', icon: LineChart },
  'journal-recap': { label: 'Recap', icon: BookOpen },
  'journal-trades': { label: 'Trades', icon: ListOrdered },
  'journal-trade-detail': { label: 'Trade Detail', icon: Activity },
  'journal-annotation': { label: 'Trade Notes', icon: MessageSquare },
  'journal-plans': { label: 'Plan vs Actual', icon: Table },
  'journal-untaken-plans': { label: 'Plans Not Taken', icon: List },
  'journal-exit-view': { label: 'Exit View', icon: BarChartHorizontalBig },
  'journal-set-and-forget': { label: 'Set and Forget', icon: GitCompareArrows },
  'journal-mistakes': { label: 'Cost of Mistakes', icon: BarChart },
  'journal-context-breakdown': { label: 'P&L by Context', icon: Layers },
  'journal-context-scatter': { label: 'Context Scatter', icon: ScatterChart },
  'journal-population': { label: 'Trader vs Market', icon: Radar },
  'journal-backtest-live': { label: 'Backtest vs Live', icon: GitCompareArrows },
  'journal-lessons': { label: 'Lessons', icon: Sparkles },
  'journal-ask': { label: 'Ask the Journal', icon: MessageSquare },
}

/**
 * Ordered section headers for the add-panel menu. Panels render grouped under
 * these labels (in this order) via `PANEL_CATEGORY`; a type with no mapping
 * falls to an unlabeled group at the end. Both the dashboard and history menus
 * draw from the same map — each only shows the categories its `availableTypes`
 * actually populate.
 */
export const PANEL_CATEGORY_ORDER = ['Core', 'Visualizations', 'Research', 'Tools'] as const

export type PanelCategory = (typeof PANEL_CATEGORY_ORDER)[number]

export const PANEL_CATEGORY: Partial<Record<ComponentType, PanelCategory>> = {
  // ── Dashboard ── (Charts folded into Core; News lives in Core)
  scanner: 'Core',
  'multi-scanner': 'Core',
  watchlist: 'Core',
  chart: 'Core',
  'multi-chart': 'Core',
  news: 'Core',
  // The dashboard menus carry only two groups, Core and Research (Chris,
  // 2026-09-15); Visualizations and Tools remain for the History types below.
  'sector-heatmap': 'Research',
  'theme-tracker': 'Research',
  'market-overwatch': 'Core',
  'key-stats': 'Core',
  'ticker-stats': 'Research',
  calendar: 'Research',
  notebook: 'Research',
  'script-editor': 'Research',
  'scanner-parity': 'Research',
  'price-alerts': 'Core',
  'alert-windows': 'Core',
  // ── History ──
  'history-records': 'Core',
  'history-stats': 'Core',
  'history-scorecard': 'Core',
  'history-chart': 'Core',
  'history-multi-chart': 'Core',
  'history-intraday': 'Core',
  'history-news': 'Core',
  'history-time-series': 'Visualizations',
  'history-bar': 'Visualizations',
  'history-bar-category': 'Visualizations',
  'history-donut-category': 'Visualizations',
  'history-scatter': 'Visualizations',
  'history-comparison': 'Visualizations',
  'history-time-distribution': 'Visualizations',
  'history-heatmap': 'Visualizations',
  'history-calendar': 'Visualizations',
  'history-sankey': 'Visualizations',
  'tags-explorer': 'Research',
}

// Order is the Panel Hub's flat two-column grid (Chris, 2026-09-15): each
// pair of lines is one row, most-added first, siblings side by side. The
// text dropdown groups these by PANEL_CATEGORY but keeps this order within
// each group.
export const DASHBOARD_TYPES: ComponentType[] = [
  'chart',
  'scanner',
  'watchlist',
  'news',
  'key-stats',
  'ticker-stats',
  'multi-chart',
  'multi-scanner',
  'market-overwatch',
  'sector-heatmap',
  'theme-tracker',
  'calendar',
  'notebook',
  // Price Alerts only on builds with the edge-realtime gate (services/realtime/flags.ts).
  ...(REALTIME_ENABLED ? (['price-alerts', 'alert-windows'] as const) : []),
  // Dev tool: offered only while devtools-scanner-parity is on (panel-rollout.ts).
  'scanner-parity',
  // No 'script-editor': EdgeScript authoring is a dock UNDER a chart, not a
  // panel of its own (Chris, 2026-09-10), so offering it here invited people to
  // place an editor with no chart to edit. The component stays registered so
  // any layout that already holds one keeps rendering.
]

/**
 * Plan display names, for upgrade copy. Mirrors `PLAN_DISPLAY_NAMES` in
 * apps/edge-user-api/edge_user_api/services/stripe_mapping.py (and the local
 * `PLAN_LABELS` on the billing page, which is not exported because a .tsx page
 * may only export components under the Fast Refresh rule).
 */
export const PLAN_DISPLAY_NAMES: Record<PlanTier, string> = {
  base: 'Base',
  basic: 'Basic',
  pro: 'Pro',
  pro_plus: 'Pro+',
  ultimate: 'Ultimate',
}

/**
 * Component types that require a minimum plan tier. `feature` is what the menu
 * actually checks against the server's granted list; `requiredTier` only names
 * the plan in the upgrade copy.
 */
export const GATED_COMPONENTS: Partial<
  Record<ComponentType, { feature: Feature; requiredTier: PlanTier }>
> = {
  'multi-scanner': { feature: 'multi-scanner', requiredTier: 'pro' },
  'multi-chart': { feature: 'multi-chart', requiredTier: 'pro' },
  'history-multi-chart': { feature: 'multi-chart', requiredTier: 'pro' },
  // Behind Pro per the 2026-09-09 entitlements sheet (audit step 6).
  'theme-tracker': { feature: 'theme-tracker', requiredTier: 'pro' },
  notebook: { feature: 'notebook', requiredTier: 'pro' },
  'history-stats': { feature: 'history-market-stats', requiredTier: 'pro' },
  'history-sankey': { feature: 'history-sankey', requiredTier: 'pro' },
}

/**
 * Max instances of a component type per layout. Expensive components (chart
 * grid, records) and fully-redundant ones (news, stats, tags, calendar) are
 * capped; lightweight/aggregate ones (scorecards, aggregate charts) can be
 * stacked. Types not listed here are unlimited.
 */
export const PANEL_LIMITS: Partial<Record<ComponentType, number>> = {
  // One market-wide reading per layout: a second copy would render the
  // identical house-computed numbers.
  'market-overwatch': 1,
  'history-multi-chart': 1,
  'history-records': 1,
  'history-news': 1,
  'history-comparison': 1,
  'history-calendar': 1,
  'history-sankey': 1,
  'tags-explorer': 1,
  'history-intraday': 1,
  'history-stats': 2,
  'history-chart': 4,
  'history-time-series': 6,
  'history-scatter': 6,
  'history-bar': 6,
  'history-bar-category': 6,
  'history-donut-category': 6,
  'history-scorecard': 20,
}

export const HISTORY_TYPES: ComponentType[] = [
  'history-records',
  'history-chart',
  'history-multi-chart',
  'history-stats',
  'history-news',
  'history-comparison',
  'history-time-series',
  'history-scatter',
  'history-bar',
  'history-bar-category',
  'history-donut-category',
  'history-intraday',
  'history-calendar',
  'history-sankey',
  'history-scorecard',
  'history-heatmap',
  'history-time-distribution',
  'tags-explorer',
]

/**
 * Backtest report panel types — shown only in the backtest report add-panel menu.
 * 'backtest-metrics' is not listed here because the strategy Report page
 * renders that strip above its mosaic; see BACKTEST_RUN_TYPES.
 */
export const BACKTEST_REPORT_TYPES: ComponentType[] = [
  'backtest-strategy-rollup',
  'backtest-overview',
  'backtest-general-metrics',
  'backtest-equity-curve',
  'backtest-performance',
  'backtest-trades',
  'backtest-calendar',
  'backtest-distributions',
  'backtest-buckets',
  'backtest-symbols',
  'backtest-risk',
  'backtest-wins-losses',
  'backtest-streaks',
  'backtest-daily-pnl',
  'backtest-win-rate',
  'backtest-monthly',
  'backtest-monthly-table',
  'backtest-hold-times',
  'backtest-dist-hour',
  'backtest-dist-day',
  'backtest-dist-duration',
  'backtest-return-histogram',
  'backtest-day-metrics',
  'backtest-rolling-windows',
]

// Per-execution run workspace: the shared report panels plus the Key Metrics
// strip (a tile here; the strategy Report page renders it above its mosaic
// instead, so it is not addable there) and two run-specific panels
// (executions list + chart grid). Consumed by RunWorkspacePage.
export const BACKTEST_RUN_TYPES: ComponentType[] = [
  'backtest-metrics',
  ...BACKTEST_REPORT_TYPES,
  'backtest-run-trades',
  'backtest-chart-grid',
  'backtest-trade-chart',
  'backtest-trade-fills',
  'backtest-filter-lab',
  'backtest-field-explorer',
  'backtest-monte-carlo',
  'backtest-monte-carlo-bands',
  'backtest-is-oos',
  'backtest-period-stability',
  'backtest-baseline',
  'backtest-sizing-settings',
  'backtest-sizing-equity',
  'backtest-not-taken',
]
